// Shared shell for the public legal pages (Privacy, Terms, Data Deletion).
// Mirrors the header style used on PlaybookPage so the legal docs feel like
// part of the same site to Facebook reviewers.

import type { ReactNode } from 'react'

interface Props {
  title: string
  subtitle?: string
  effectiveDate: string
  children: ReactNode
}

export function LegalLayout({ title, subtitle, effectiveDate, children }: Props) {
  return (
    <div className="min-h-screen bg-jarvis-dark text-jarvis-text">
      <header className="border-b border-jarvis-border/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="text-jarvis-blue font-semibold tracking-widest text-sm">
            JARVIS
          </a>
          <a
            href="/login"
            className="text-xs text-jarvis-muted hover:text-jarvis-text transition-colors"
          >
            Sign in →
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">{title}</h1>
        {subtitle && (
          <p className="text-jarvis-muted text-sm mb-1">{subtitle}</p>
        )}
        <p className="text-xs text-jarvis-muted uppercase tracking-widest mb-10">
          Effective {effectiveDate}
        </p>
        <div className="prose-jarvis space-y-6 leading-relaxed text-[15px]">
          {children}
        </div>
        <footer className="mt-16 pt-6 border-t border-jarvis-border/50 text-xs text-jarvis-muted">
          <p>
            ClearFinOps LLC · Lehi, Utah, USA · ClearFinOps LLC operates the Jarvis service
            and the <a href="https://yourproject.ai" className="text-jarvis-blue hover:underline">yourproject.ai</a> brand.
          </p>
          <nav className="mt-2 flex gap-4">
            <a href="/privacy" className="hover:text-jarvis-text transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-jarvis-text transition-colors">Terms</a>
            <a href="/data-deletion" className="hover:text-jarvis-text transition-colors">Data Deletion</a>
            <a href="mailto:kc@yourproject.ai" className="hover:text-jarvis-text transition-colors">Contact</a>
          </nav>
        </footer>
      </main>
    </div>
  )
}

export function Section({ id, heading, children }: { id?: string; heading: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-semibold tracking-tight mb-2 mt-8">{heading}</h2>
      <div className="space-y-3 text-jarvis-text/90">{children}</div>
    </section>
  )
}
